import { t } from "@/lib/i18n/interpolate";
import type {
  DocBlock,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";
import {
  fieldOrPrompt,
  leadInItem,
  paragraphsOrPrompt,
  splitItems,
} from "@/lib/tool-kit/essay-templates/text";

/**
 * A character sheet for fiction: the basics, then the parts that drive a story — what
 * the character wants, why, what they fear, the flaw that stands in the way and the
 * arc that tests it — with an optional set of interview questions for going deeper.
 */

export const CHARACTER_ROLES = [
  "protagonist",
  "antagonist",
  "supporting",
  "mentor",
  "love-interest",
  "foil",
  "narrator",
] as const;

export type CharacterRole = (typeof CHARACTER_ROLES)[number] | "";

export const ARC_TYPES = ["positive", "negative", "flat"] as const;

export type ArcType = (typeof ARC_TYPES)[number] | "";

export interface CharacterProfileInput {
  name: string;
  age: string;
  role: CharacterRole;
  occupation: string;
  home: string;
  appearance: string;
  personality: string;
  backstory: string;
  goal: string;
  motivation: string;
  fear: string;
  flaw: string;
  arc: ArcType;
  arcNotes: string;
  /** One per line: "Tomas Reyes — her closest ally". */
  relationships: string;
  voice: string;
  questions: boolean;
}

export interface CharacterProfileLabels {
  defaultTitle: string;
  titleWithName: string;
  headings: {
    glance: string;
    appearance: string;
    personality: string;
    backstory: string;
    drive: string;
    flaw: string;
    arc: string;
    relationships: string;
    voice: string;
    questions: string;
  };
  fields: {
    name: string;
    age: string;
    role: string;
    occupation: string;
    home: string;
    goal: string;
    motivation: string;
    fear: string;
  };
  roles: Record<(typeof CHARACTER_ROLES)[number], string>;
  arcs: Record<(typeof ARC_TYPES)[number], string>;
  prompts: {
    appearance: string;
    personality: string;
    backstory: string;
    goal: string;
    motivation: string;
    fear: string;
    flaw: string;
    arc: string;
    relationships: string;
    voice: string;
  };
  questionsNote: string;
  questions: readonly string[];
}

function glance(
  input: CharacterProfileInput,
  labels: CharacterProfileLabels
): DocSection {
  return {
    heading: labels.headings.glance,
    blocks: [
      {
        kind: "fields",
        fields: [
          { label: labels.fields.name, value: input.name.trim() },
          { label: labels.fields.age, value: input.age.trim() },
          {
            label: labels.fields.role,
            value: input.role ? labels.roles[input.role] : "",
          },
          { label: labels.fields.occupation, value: input.occupation.trim() },
          { label: labels.fields.home, value: input.home.trim() },
        ],
      },
    ],
  };
}

function relationships(
  input: CharacterProfileInput,
  labels: CharacterProfileLabels
): DocBlock[] {
  const lines = splitItems(input.relationships);
  if (lines.length === 0) {
    return [{ kind: "prompt", text: labels.prompts.relationships }];
  }
  return [
    {
      kind: "list",
      list: {
        style: "bullet",
        items: lines.map((line) => ({ text: leadInItem(line) })),
      },
    },
  ];
}

export function buildCharacterProfile(
  input: CharacterProfileInput,
  labels: CharacterProfileLabels
): DocumentModel {
  const { headings, prompts } = labels;
  const name = input.name.trim();
  const arc: DocBlock[] = input.arc
    ? [{ kind: "guidance", text: labels.arcs[input.arc] }]
    : [];
  const questions: DocSection[] = input.questions
    ? [
        {
          heading: headings.questions,
          blocks: [
            { kind: "guidance", text: labels.questionsNote },
            {
              kind: "list",
              list: {
                style: "decimal",
                items: labels.questions.map((text) => ({ text })),
              },
            },
          ],
        },
      ]
    : [];
  return {
    title: name ? t(labels.titleWithName, { name }) : labels.defaultTitle,
    sections: [
      glance(input, labels),
      {
        heading: headings.appearance,
        blocks: paragraphsOrPrompt(input.appearance, prompts.appearance),
      },
      {
        heading: headings.personality,
        blocks: paragraphsOrPrompt(input.personality, prompts.personality),
      },
      {
        heading: headings.backstory,
        blocks: paragraphsOrPrompt(input.backstory, prompts.backstory),
      },
      {
        heading: headings.drive,
        blocks: [
          fieldOrPrompt(labels.fields.goal, input.goal, prompts.goal),
          fieldOrPrompt(
            labels.fields.motivation,
            input.motivation,
            prompts.motivation
          ),
          fieldOrPrompt(labels.fields.fear, input.fear, prompts.fear),
        ],
      },
      {
        heading: headings.flaw,
        blocks: paragraphsOrPrompt(input.flaw, prompts.flaw),
      },
      {
        heading: headings.arc,
        blocks: [...arc, ...paragraphsOrPrompt(input.arcNotes, prompts.arc)],
      },
      {
        heading: headings.relationships,
        blocks: relationships(input, labels),
      },
      {
        heading: headings.voice,
        blocks: paragraphsOrPrompt(input.voice, prompts.voice),
      },
      ...questions,
    ],
  };
}
